import {Button, icons} from '@superweb/ui'
import {useTransition} from '@yandex-delivery/blocks'
import {Tooltip, useTooltip} from '@yandex-delivery/ui'
import clsx from 'clsx'
import React, {useCallback, useMemo, useRef} from 'react'

import {useMapContextValue} from '../../../context'
import type {LngLat, MapGeolocationControlProps} from '../../../model'
import {MapMarker} from '../../index'
import {MapIcon} from '../components'
import {getGeolocationFromBrowser} from '../lib'
import styleClasses from './classes.module.css'

type OnGeolocatePositionType = MapGeolocationControlProps['onGeolocatePosition']

type Props = {
  classes?: {
    root?: string
    tooltip?: string
  }
  initialLocation?: LngLat
  /**
   * Определять локацию на маунте
   */
  initializeLocation?: boolean
  onGeolocatePosition?: OnGeolocatePositionType
  onGeolocateError?: (error: GeolocationPositionError) => void
  translations?: {
    geolocationError?: string
  }
}

const GEOLOCATION_KEY = 'GEOLOCATION_KEY'
const DEFAULT_TRANSLATIONS: Props['translations'] = {
  geolocationError: 'Geolocation is not available',
}

type GeoPositionType = {
  coords: LngLat
  accuracy?: number
}

function MapGeolocationCustomControl({
  initialLocation,
  initializeLocation,
  onGeolocatePosition,
  onGeolocateError,
  translations = {},
  ...props
}: Props) {
  const mapContext = useMapContextValue()
  const t = useMemo(() => ({...DEFAULT_TRANSLATIONS, ...translations}), [translations])
  const [{isPending: isLoading, error, payload: location = initialLocation}, startTransition] = useTransition<
    GeoPositionType['coords'],
    GeolocationPositionError
  >(
    initializeLocation
      ? () =>
          getGeolocationFromBrowser()
            .then(lngLat => {
              onGeolocatePosition?.(lngLat)
              return lngLat
            })
            .catch(e => {
              if (initialLocation) {
                onGeolocatePosition?.(initialLocation)
              }
              onGeolocateError?.(e)
              throw e
            })
      : undefined,
    {
      key: GEOLOCATION_KEY,
      shouldShowBaseError: false,
    },
  )
  const iconName = mapContext?.lang === 'ru_RU' ? 'ya-ru' : 'ya-en'

  const clickHandler = useCallback(() => {
    return startTransition(getGeolocationFromBrowser, {
      onCommit: lngLat => {
        onGeolocatePosition?.(lngLat)
      },
      onError: (err: GeolocationPositionError) => {
        onGeolocateError?.(err)

        if (onGeolocatePosition && initialLocation) {
          onGeolocatePosition(initialLocation)
        }
      },
    })
  }, [initialLocation, onGeolocateError, onGeolocatePosition])

  // Отображаем в tooltip'е ошибку
  const tooltipRef = useRef<HTMLDivElement>(null)
  const {tooltipProps, triggerProps, state} = useTooltip(tooltipRef)
  if (state.isOpen !== !!error) {
    if (!!error) state.open()
    else state.close()
  }

  return (
    <>
      {state.isOpen && (
        <Tooltip placement="start" state={state} targetRef={tooltipRef} tooltipProps={tooltipProps}>
          {t.geolocationError ?? 'Ошибка'}
        </Tooltip>
      )}

      <div {...triggerProps} ref={tooltipRef} className={clsx(styleClasses.container, props.classes?.root)}>
        <Button icon={icons.LocationFill} onPress={clickHandler} progress={isLoading} shape="circle" view="floating" />
      </div>

      {!!location && mapContext?.components?.YMapMarker && (
        <MapMarker coordinates={location} zIndex={-1}>
          <div className={styleClasses.iconWrap}>
            <MapIcon icon={iconName} />
          </div>
        </MapMarker>
      )}
    </>
  )
}

export default React.memo(MapGeolocationCustomControl)
